// TelcoTrack — MUK cassette-rack visual numbering (physical convention).
// Stored `position` stays 0-based per section. Display / (row, col) are derived here.
// D5 occupancy IDs stay 1–24 per row (`rozvadecCassetteDisplayPosition`); do not use this
// helper for RAY-TR3 / PON32.

import { rozvadecCassetteOccupiedPositions } from './rozvadec-cassette.constants';

/** Visual rows in one MUK cassette column. Row 0 is the top. */
export const ROZVADEC_MUK_RACK_ROWS = 24;

export type RozvadecMukRackColumns = 1 | 2;

export type RozvadecMukRackCell = {
  row: number;
  col: number;
  /** 1-based physical position. */
  position: number;
  sectionId: string;
};

/**
 * Visual column for a MUK rack section. `0` = left, `1` = right.
 * MUK 1×24 has only `rada_1` (col 0). MUK 2×24: `rada_1` left, `rada_2` right.
 */
export function rozvadecMukRackSectionCol(
  typeId: string | null | undefined,
  sectionId: string | null | undefined,
): number | null {
  const columns = rozvadecMukRackColumns(typeId);
  if (columns == null) return null;
  const id = (sectionId ?? '').trim();
  if (columns === 1) return id === 'rada_1' ? 0 : null;
  if (id === 'rada_1') return 0;
  if (id === 'rada_2') return 1;
  return null;
}

export function rozvadecMukRackColumns(
  typeId: string | null | undefined,
): RozvadecMukRackColumns | null {
  const id = (typeId ?? '').trim();
  if (id === 'MUK_1X24') return 1;
  if (id === 'MUK_2X24') return 2;
  return null;
}

/**
 * `(row, col) →` 1-based physical position.
 *
 * - MUK 1×24 (`columns === 1`): top-left is **1**, bottom is **24**.
 * - MUK 2×24 (`columns === 2`): top-left `(0, 0)` is **1**; top-right `(0, 1)` is **25**.
 *
 * ```
 * Left          Right
 * 1             25
 * 2             26
 * …             …
 * 24            48
 * ```
 */
export function rozvadecMukRackCellPosition(
  row: number,
  col: number,
  columns: RozvadecMukRackColumns,
): number | null {
  if (!Number.isInteger(row) || row < 0 || row >= ROZVADEC_MUK_RACK_ROWS) return null;
  if (!Number.isInteger(col) || col < 0 || col >= columns) return null;
  if (columns === 1) return row + 1;
  return col === 0 ? row + 1 : row + 1 + ROZVADEC_MUK_RACK_ROWS;
}

export function rozvadecMukRackCellFromPosition(
  position: number,
  columns: RozvadecMukRackColumns,
): { row: number; col: number } | null {
  if (!Number.isInteger(position) || position < 1) return null;
  if (columns === 1) {
    if (position > ROZVADEC_MUK_RACK_ROWS) return null;
    return { row: position - 1, col: 0 };
  }
  if (position <= ROZVADEC_MUK_RACK_ROWS) {
    return { row: position - 1, col: 0 };
  }
  if (position <= ROZVADEC_MUK_RACK_ROWS * 2) {
    return { row: position - 1 - ROZVADEC_MUK_RACK_ROWS, col: 1 };
  }
  return null;
}

/**
 * 1-based physical label for a 0-based section slot.
 * `fillFrom: 'top'` means slot 0 is the top row, so `position` === visual `row`.
 * Non-MUK racks (PON32, …) fall back to `position + 1`.
 */
export function rozvadecMukRackDisplayNumber(
  typeId: string | null | undefined,
  sectionId: string | null | undefined,
  position: number,
): number | null {
  if (!Number.isInteger(position) || position < 0) return null;
  const columns = rozvadecMukRackColumns(typeId);
  const col = rozvadecMukRackSectionCol(typeId, sectionId);
  if (columns != null && col != null) {
    return rozvadecMukRackCellPosition(position, col, columns);
  }
  return position + 1;
}

/**
 * Physical numbers occupied by a cassette. KM5 spans two consecutive numbers
 * (1–2 at top-left, 25–26 at top-right). The label is the upper / smaller one.
 */
export function rozvadecMukRackOccupiedDisplayNumbers(
  typeId: string | null | undefined,
  sectionId: string | null | undefined,
  cassetteType: string | null | undefined,
  position: number,
): number[] {
  return rozvadecCassetteOccupiedPositions(cassetteType, position)
    .map((cell) => rozvadecMukRackDisplayNumber(typeId, sectionId, cell))
    .filter((value): value is number => value != null);
}

/**
 * Physical fill / numbering walk: MUK24 `rada_1` 0…23; MUK48 left (`rada_1`)
 * then right (`rada_2`). Empty for non-MUK types.
 */
export function rozvadecMukRackNumberingCells(
  typeId: string | null | undefined,
): readonly { sectionId: string; position: number }[] {
  const columns = rozvadecMukRackColumns(typeId);
  if (columns === 1) {
    return Array.from({ length: ROZVADEC_MUK_RACK_ROWS }, (_, position) => ({
      sectionId: 'rada_1',
      position,
    }));
  }
  if (columns === 2) {
    return [
      ...Array.from({ length: ROZVADEC_MUK_RACK_ROWS }, (_, position) => ({
        sectionId: 'rada_1',
        position,
      })),
      ...Array.from({ length: ROZVADEC_MUK_RACK_ROWS }, (_, position) => ({
        sectionId: 'rada_2',
        position,
      })),
    ];
  }
  return [];
}
